// PHASE_0 B0 deliverable 2 — zod-driven entity form primitive.
//
// The single canonical create/edit form surface P1+ entity screens reuse
// ([D6]). It is schema-validated on submit: the host passes a validator with
// a Zod-style `safeParse` shape, so the component is Zod-compatible without
// the shared library taking a hard zod dependency (the consuming app owns
// the schema — e.g. the @novemai/hris-sdk validators). On a failed parse it
// surfaces per-field error messages (already-localized by the host); on a
// successful parse it hands the typed, validated value to `onSubmit`.
//
// Fields are declarative; the actual inputs are caller-rendered so the form
// composes the existing Input/Textarea/etc. primitives and keeps every
// label/placeholder an i18n key at the call site. The component owns the
// <form>, the submit/validation wiring, and error association (aria-describedby).

import {
  forwardRef,
  useState,
  type FormHTMLAttributes,
  type ReactNode,
} from "react";

import { cx } from "../lib/cx.ts";

/**
 * Minimal Zod-compatible validation port. A Zod schema satisfies this via
 * its `safeParse`; any equivalent validator works too. Keeps the shared
 * library free of a direct zod dependency.
 */
export interface SafeParseValidator<TValue> {
  safeParse: (input: unknown) =>
    | { readonly success: true; readonly data: TValue }
    | {
        readonly success: false;
        readonly error: {
          readonly issues: ReadonlyArray<{
            readonly path: ReadonlyArray<string | number>;
            readonly message: string;
          }>;
        };
      };
}

/** Per-field error map keyed by field name; produced from the validator's issues. */
export type FieldErrors = Readonly<Record<string, string>>;

export interface EntityFormRenderArgs {
  /** Current per-field errors (empty until the first failed submit). */
  readonly errors: FieldErrors;
  /** True while onSubmit is in flight. */
  readonly submitting: boolean;
}

export interface EntityFormProps<TValue>
  extends Omit<FormHTMLAttributes<HTMLFormElement>, "onSubmit" | "children"> {
  /** Zod-style validator run against the collected FormData on submit. */
  readonly validator: SafeParseValidator<TValue>;
  /**
   * Convert the native FormData into the raw object handed to the validator.
   * Defaults to `Object.fromEntries` (string fields). Override for coercion
   * (numbers, checkboxes, nested shapes).
   */
  readonly collect?: (formData: FormData) => unknown;
  /** Called with the validated, typed value on a successful parse. */
  readonly onValidSubmit: (value: TValue) => void | Promise<void>;
  /** Field renderer — receives the current error map + submitting flag. */
  readonly children: (args: EntityFormRenderArgs) => ReactNode;
}

function defaultCollect(formData: FormData): unknown {
  return Object.fromEntries(formData.entries());
}

function issuesToFieldErrors(
  issues: ReadonlyArray<{ readonly path: ReadonlyArray<string | number>; readonly message: string }>,
): FieldErrors {
  const map: Record<string, string> = {};
  for (const issue of issues) {
    // First path segment is the field name; keep the first message per field.
    const field = issue.path[0];
    const key = field === undefined ? "_form" : String(field);
    if (map[key] === undefined) map[key] = issue.message;
  }
  return map;
}

function EntityFormInner<TValue>(
  props: EntityFormProps<TValue>,
  ref: React.ForwardedRef<HTMLFormElement>,
): React.ReactElement {
  const {
    validator,
    collect = defaultCollect,
    onValidSubmit,
    children,
    className,
    ...rest
  } = props;

  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    const raw = collect(new FormData(event.currentTarget));
    const result = validator.safeParse(raw);
    if (!result.success) {
      setErrors(issuesToFieldErrors(result.error.issues));
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      await onValidSubmit(result.data);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      ref={ref}
      noValidate
      data-component="entity-form"
      className={cx("nai-entity-form", className)}
      onSubmit={(e): void => {
        void handleSubmit(e);
      }}
      {...rest}
    >
      {children({ errors, submitting })}
    </form>
  );
}

/** Generic forwardRef wrapper — cast preserves the `<TValue>` generic. */
export const EntityForm = forwardRef(EntityFormInner) as <TValue>(
  props: EntityFormProps<TValue> & { ref?: React.ForwardedRef<HTMLFormElement> },
) => React.ReactElement;
